import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { DatePickerModule } from 'primeng/datepicker';
import { getFieldKey, formatLabel, formatFecha } from '../../utils/utils';
import { diasHabilesDesde, ES_DATE_LOCALE } from '../../utils/timeZone';
import { MatDialog } from '@angular/material/dialog';
import { ApelacionComponent } from './apelacion/apelacion.component';
import { ParentMessagesSender } from '../../services/parent-messages-sender';

@Component({
  selector: 'app-ver-caso',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, DatePickerModule],
  templateUrl: 'ver-caso.component.html',
  styleUrls: ['ver-caso.component.scss'],
})
export class VerCasoComponent implements OnChanges {
  @Input() caso: any = null;
  @Input() subcategoria: any = null;
  @Input() categoria: string = '';
  @Input() categoriaType: string = '';
  @Input() selectedEstacion: {
    label: string;
    value: number;
    desde: Date;
  } | null = null;
  @Input() ocultarApelacionNoDisponible = false;
  @Output() closeVerCaso = new EventEmitter<void>();
  @Output() apelacionIngresada = new EventEmitter<{
    originalCaseNumber: string;
    nuevoCaseNumber?: string;
  }>();

  es = ES_DATE_LOCALE;

  constructor(
    private dialog: MatDialog,
    private parentMessagesSender: ParentMessagesSender,
  ) {}

  isApelacionDialogOpen: boolean = false;
  diasDesdeIngreso: number = 100;
  heridos: 'Si' | 'No' = 'No';
  userValues: Record<string, string> = {
    nombres: '',
    apellidos: '',
    email: '',
    telefono: '',
  };
  formValues: Record<string, any> = {};
  displayFields: any[] = [];
  allFields: any[] = [];

  halfWidthFields: Array<{ displayLabel: string; key: string }> = [
    { displayLabel: 'NOMBRES', key: 'nombres' },
    { displayLabel: 'APELLIDOS', key: 'apellidos' },
    { displayLabel: 'EMAIL', key: 'email' },
    { displayLabel: 'TELÉFONO', key: 'telefono' },
  ];

  getFieldKey = getFieldKey;
  formatLabel = formatLabel;
  formatFechaCaso = (fecha?: string | null): string =>
    fecha ? formatFecha(fecha) : '';

  getReferenceDisplayValue(field: any): string {
    const fieldKey = getFieldKey(field);
    const rawValue = this.formValues?.[fieldKey];
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return '';
    }

    const values = Array.isArray(field?.picklist_values)
      ? field.picklist_values
      : [];

    const rawString = String(rawValue);
    const match = values.find((v: any) => {
      const id = v?.id;
      if (id === null || id === undefined) return false;
      return String(id) === rawString;
    });

    const display =
      match?.name ?? match?.label ?? match?.value ?? match?.descripcion ?? null;

    return display !== null && display !== undefined && display !== ''
      ? String(display)
      : rawString;
  }

  get canApelar(): boolean {
    return (
      this.categoriaType !== 'Siniestro' &&
      this.isSubcategoriaActiva &&
      this.caso?.Status == 'Resuelto' &&
      this.caso?.Origin == 'Portal Concesionarios' &&
      !this.caso?.numeroCasoReferenciado &&
      this.diasDesdeIngreso <= 3 &&
      !this.caso?.hasApelacion
    );
  }

  get isSubcategoriaActiva(): boolean {
    if (!this.subcategoria) return false;
    return (this.subcategoria.active ?? true) === true;
  }

  get cancelButtonLabel(): string {
    return this.canApelar ? 'Cancelar' : 'Volver';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['subcategoria']) {
      const fields = (this.subcategoria?.fields ?? []) as any[];
      this.allFields = fields;
      this.updateDisplayFields();
    }
    if (changes['caso']) {
      this.loadCasoData();

      const closedDate = this.caso?.ClosedDate;
      this.diasDesdeIngreso = closedDate
        ? diasHabilesDesde(new Date(closedDate))
        : 100;
    }
  }

  openApelacionDialog(): void {
    if (!this.canApelar) return;

    this.isApelacionDialogOpen = true;
    this.parentMessagesSender.showGreyBackground();
    const ref = this.dialog.open(ApelacionComponent, {
      width: '360px',
      height: '917px',
      backdropClass: 'custom-backdrop',
      position: {
        top: '20px',
        left: '0px',
      },
      data: {
        caseNumber: String(this.caso?.CaseNumber ?? ''),
        caso: this.caso,
        categoriaType: this.categoriaType,
        categoria: this.categoria,
        subcategoria: this.subcategoria,
        selectedEstacion: this.selectedEstacion,
        displayFields: this.displayFields,
        formValues: this.formValues,
        userValues: this.userValues,
        heridos: this.heridos,
      },
    });
    ref.afterClosed().subscribe((result: any) => {
      this.isApelacionDialogOpen = false;
      this.parentMessagesSender.hideGreyBackground();

      if (result?.success) {
        this.apelacionIngresada.emit({
          originalCaseNumber: String(this.caso?.CaseNumber ?? ''),
          nuevoCaseNumber: result?.numeroCaso ? String(result.numeroCaso) : '',
        });
      }
    });
  }

  private updateDisplayFields(): void {
    const fields = this.allFields;
    const baseFields = fields.filter((f) => {
      const campoPadre = f?.campo_padre;
      return (
        campoPadre === null || campoPadre === undefined || campoPadre === ''
      );
    });

    const processedFields = baseFields
      .filter(
        (f) =>
          f?.field_name !== 'ArmasHeridos__c' &&
          f?.field_name !== 'EstacionDeServicio__c',
      )
      .map((f) => ({
        ...f,
        displayLabel: formatLabel(f?.label ?? ''),
        required: this.subcategoria?.compulsory_fields?.includes(f?.field_name),
      }));

    const dependentFields: any[] = [];
    const visibleDependentFieldNames = new Set<string>();

    for (const field of baseFields) {
      if (
        field?.field_type === 'picklist' &&
        field?.desencadenante &&
        field?.subcampo
      ) {
        const fieldKey = getFieldKey(field);
        const selectedValue = this.formValues[fieldKey];

        if (selectedValue) {
          const desencadenante = Array.isArray(field.desencadenante)
            ? field.desencadenante
            : [field.desencadenante];

          if (desencadenante.includes(selectedValue)) {
            const childField = fields.find(
              (f) => f?.field_name === field.subcampo,
            );
            if (childField) {
              visibleDependentFieldNames.add(childField.field_name);
              if (
                !dependentFields.some(
                  (f) => f?.field_name === childField.field_name,
                )
              ) {
                dependentFields.push({
                  ...childField,
                  displayLabel: formatLabel(childField?.label ?? ''),
                  required: this.subcategoria?.compulsory_fields?.includes(
                    childField?.field_name,
                  ),
                });
              }
            }
          }
        }
      }
    }

    const allFields = [...processedFields, ...dependentFields];
    allFields.sort((a: any, b: any) => {
      const sa = typeof a?.sort === 'number' ? a.sort : 0;
      const sb = typeof b?.sort === 'number' ? b.sort : 0;
      return sa - sb;
    });
    this.displayFields = allFields;
  }

  private loadCasoData(): void {
    if (!this.caso) return;

    this.userValues = {
      nombres: '',
      apellidos: '',
      email: '',
      telefono: '',
    };
    this.formValues = {};
    this.heridos = 'No';

    if (this.caso.cliente) {
      this.userValues['nombres'] = String(this.caso.nombres || '');
      this.userValues['apellidos'] = String(this.caso.apellidos || '');
      this.userValues['email'] = String(this.caso.ContactEmail || '');
      this.userValues['telefono'] = String(this.caso.TelefonoCliente__c || '');
    }

    if (this.categoriaType?.toLowerCase() === 'siniestro') {
      this.heridos =
        this.caso.ArmasHeridos__c === 'Si' ||
        this.caso.heridos === 'Si' ||
        this.caso.Heridos__c === 'Si'
          ? 'Si'
          : 'No';
    }

    if (Array.isArray(this.caso.parametros)) {
      this.caso.parametros.forEach((param: any) => {
        if (
          param.campo &&
          param.contenido !== undefined &&
          param.contenido !== null
        ) {
          const fieldKey = param.campo;
          let value = param.contenido;

          if (typeof value === 'string') {
            if (value.startsWith('+569')) {
              value = value.substring(4);
            }
            const field = this.allFields.find(
              (f) => f?.field_name === fieldKey,
            );
            if (field?.field_type === 'multipicklist' && value.includes(';')) {
              value = value.split(';');
            }
          }

          this.formValues[fieldKey] = value;
        }
      });
    } else {
      this.allFields.forEach((field) => {
        const fieldName = field?.field_name;
        if (!fieldName) return;

        let value =
          this.caso[fieldName] ||
          this.caso[fieldName.replace('__c', '')] ||
          null;

        if (value !== null && value !== undefined) {
          if (typeof value === 'string') {
            if (field.field_type === 'phone' && value.startsWith('+569')) {
              value = value.substring(4);
            }
            if (field.field_type === 'multipicklist' && value.includes(';')) {
              value = value.split(';');
            }
            if (
              field.field_type === 'date' ||
              field.field_type === 'datetime'
            ) {
              const iso = String(value).replace('+0000', 'Z');
              value = new Date(iso);
            }
          }

          this.formValues[fieldName] = value;
        }
      });
    }

    this.updateDisplayFields();
  }

  onCancel(): void {
    this.closeVerCaso.emit();
  }
}
